import React from "react";

import { useTranslation } from "react-i18next";

import AddToLibraryButton from "../../../../../../components/ui/add-to-library-button/AddToLibraryButton";
import ButtonGroup from "../../../../../../components/ui/button-group/ButtonGroup";
import ColorPicker from "../../../../../../components/ui/color-picker/ColorPicker";
import IconPicker from "../../../../../../components/ui/icon-picker/IconPicker";
import Text from "../../../../../../components/ui/input/text/Input";
import LinkPicker from "../../../../../../components/ui/link-picker/LinkPicker";
import { buttonLibraryService } from "@Shared/Service/ButtonLibraryService";
import { isEqual } from "lodash";
import { useState } from "react";
import { useOpticsStore } from "../../../../../../stores/use-optics-store";
import {
  OptionContainer,
  OptionContent,
  OptionLabel,
  SettingsOptionContainer,
  SettingsOptionHeading,
} from "../../styles";
import { BUTTON_PROP, BUTTON_TYPE, LIBRARY_SERVICE_ACTION } from "./defaults";
import {
  ALIGNMENT_OPTIONS,
  BOOLEAN_OPTIONS,
  CORNERS_OPTIONS,
  ICON_POSITION_OPTIONS,
  SIZE_OPTIONS,
} from "./options";
import { buttonStores } from "../../../../../../stores/export-stores";
import { MACRO_KEY } from "../../../../../defaults";

const ButtonSettings = ({ editorMacro }) => {
  const { t } = useTranslation();

  const [isRequestingToLibrary, setIsRequestingToLibrary] = useState(false);

  const { libraryButtons, setLibraryButtons, hasStyleguidePermission } =
    useOpticsStore((state) => ({
      libraryButtons: state.libraryButtons,
      setLibraryButtons: state.setLibraryButtons,
      hasStyleguidePermission: state.hasStyleguidePermission,
    }));

  const { data, setData } = buttonStores[editorMacro]((state) => ({
    data: state.data,
    setData: state.setData,
  }));

  const selectedLibraryButton = libraryButtons.find(
    (btn) => btn.id === data.id
  );

  const hasIcon = [BUTTON_TYPE.ICON, BUTTON_TYPE.TEXT_AND_ICON].includes(
    data.type
  );

  const hasIconPosition = data.type !== BUTTON_TYPE.ICON;

  const handleAccentColorChange = ({ accent, text }) => {
    setData({
      [BUTTON_PROP.ACCENT_COLOR]: text,
      [BUTTON_PROP.LABEL_COLOR]: text,
      [BUTTON_PROP.BACKGROUND_COLOR]: accent,
      [BUTTON_PROP.BORDER]: accent,
    });
  };

  const handleButtonLibraryRequest = async (fnName) => {
    try {
      setIsRequestingToLibrary(true);

      const response = await buttonLibraryService[fnName](data, "macro");
      if (response?.data?.btnId) {
        setData({ id: response.data.btnId });
        setLibraryButtons([
          ...libraryButtons,
          { ...data, id: response.data.btnId },
        ]);
      } else {
        setLibraryButtons([
          ...libraryButtons.map((btn) =>
            btn.id === data.id ? { ...data } : btn
          ),
        ]);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsRequestingToLibrary(false);
    }
  };

  return (
    <>
      {editorMacro === MACRO_KEY.BUTTON && (
        <SettingsOptionContainer>
          <SettingsOptionHeading>{t("alignment")}</SettingsOptionHeading>
          <OptionContainer>
            <OptionLabel>{t("align")}</OptionLabel>
            <OptionContent>
              <ButtonGroup
                items={ALIGNMENT_OPTIONS}
                value={data[BUTTON_PROP.ALIGN]}
                onClick={(value) => setData({ [BUTTON_PROP.ALIGN]: value })}
              />
            </OptionContent>
          </OptionContainer>
        </SettingsOptionContainer>
      )}
      <SettingsOptionContainer>
        <SettingsOptionHeading>
          {t("flexible-display-items")}
        </SettingsOptionHeading>
        <OptionContainer>
          <OptionLabel>{t("icon")}</OptionLabel>
          <OptionContent>
            <ButtonGroup
              items={BOOLEAN_OPTIONS}
              value={
                data[BUTTON_PROP.TYPE] === BUTTON_TYPE.ICON ||
                data[BUTTON_PROP.TYPE] === BUTTON_TYPE.TEXT_AND_ICON
              }
              onClick={(isOn) => {
                const type = isOn
                  ? data[BUTTON_PROP.TYPE] === BUTTON_TYPE.TEXT
                    ? BUTTON_TYPE.TEXT_AND_ICON
                    : BUTTON_TYPE.ICON
                  : BUTTON_TYPE.TEXT;

                setData({ [BUTTON_PROP.TYPE]: type });
              }}
            />
          </OptionContent>
        </OptionContainer>
        <OptionContainer>
          <OptionLabel>{t("label")}</OptionLabel>
          <OptionContent>
            <ButtonGroup
              items={BOOLEAN_OPTIONS}
              value={
                data[BUTTON_PROP.TYPE] === BUTTON_TYPE.TEXT ||
                data[BUTTON_PROP.TYPE] === BUTTON_TYPE.TEXT_AND_ICON
              }
              onClick={(isOn) => {
                const type = isOn
                  ? data[BUTTON_PROP.TYPE] === BUTTON_TYPE.ICON
                    ? BUTTON_TYPE.TEXT_AND_ICON
                    : BUTTON_TYPE.TEXT
                  : BUTTON_TYPE.ICON;

                setData({ [BUTTON_PROP.TYPE]: type });
              }}
            />
          </OptionContent>
        </OptionContainer>
      </SettingsOptionContainer>
      <SettingsOptionContainer>
        <SettingsOptionHeading>{t("link")}</SettingsOptionHeading>
        <OptionContainer>
          <OptionLabel alignToTop>{t("link-to")}</OptionLabel>
          <OptionContent>
            <LinkPicker
              value={data[BUTTON_PROP.URL]}
              onChange={(value) => setData({ [BUTTON_PROP.URL]: value })}
            />
          </OptionContent>
        </OptionContainer>
        <OptionContainer>
          <OptionLabel>{t("new-tab")}</OptionLabel>
          <OptionContent>
            <ButtonGroup
              items={BOOLEAN_OPTIONS}
              value={data[BUTTON_PROP.NEW_TAB]}
              onClick={(value) => setData({ [BUTTON_PROP.NEW_TAB]: value })}
            />
          </OptionContent>
        </OptionContainer>
      </SettingsOptionContainer>
      <SettingsOptionContainer>
        <SettingsOptionHeading>{t("content")}</SettingsOptionHeading>
        {data[BUTTON_PROP.TYPE] !== BUTTON_TYPE.ICON && (
          <OptionContainer>
            <OptionLabel>{t("label")}</OptionLabel>
            <OptionContent>
              <Text
                placeholder="Add..."
                value={data[BUTTON_PROP.LABEL]}
                onChange={(value) => setData({ [BUTTON_PROP.LABEL]: value })}
              />
            </OptionContent>
          </OptionContainer>
        )}
        {hasIcon && (
          <OptionContainer>
            <OptionLabel>{t("icon")}</OptionLabel>
            <OptionContent>
              <IconPicker
                defaultTab={
                  typeof data[BUTTON_PROP.ICON] === "object"
                    ? "Emojis"
                    : "Icons"
                }
                tabs={["Icons", "Emojis", "Logos"]}
                icon={data[BUTTON_PROP.ICON]}
                onChange={({ value }) => setData({ [BUTTON_PROP.ICON]: value })}
              />
            </OptionContent>
          </OptionContainer>
        )}
      </SettingsOptionContainer>
      <SettingsOptionContainer>
        <SettingsOptionHeading>{t("styling-options")}</SettingsOptionHeading>
        <OptionContainer>
          <OptionLabel>{t("corners")}</OptionLabel>
          <OptionContent>
            <ButtonGroup
              items={CORNERS_OPTIONS}
              value={data[BUTTON_PROP.CORNERS]}
              options={{ input: true }}
              onClick={(value) => setData({ [BUTTON_PROP.CORNERS]: value })}
              onChange={({ target: { value } }) =>
                setData({ [BUTTON_PROP.CORNERS]: value })
              }
            />
          </OptionContent>
        </OptionContainer>
        <OptionContainer>
          <OptionLabel>{t("size")}</OptionLabel>
          <OptionContent>
            <ButtonGroup
              items={SIZE_OPTIONS}
              value={data[BUTTON_PROP.SIZE]}
              onClick={(value) => setData({ [BUTTON_PROP.SIZE]: value })}
            />
          </OptionContent>
        </OptionContainer>
        {hasIcon && hasIconPosition && (
          <OptionContainer>
            <OptionLabel>{t("icon")}</OptionLabel>
            <OptionContent>
              <ButtonGroup
                items={ICON_POSITION_OPTIONS}
                value={data[BUTTON_PROP.ICON_POSITION]}
                onClick={(value) =>
                  setData({ [BUTTON_PROP.ICON_POSITION]: value })
                }
              />
            </OptionContent>
          </OptionContainer>
        )}
        <OptionContainer>
          <OptionLabel alignToTop>{t("colors")}</OptionLabel>
          <OptionContent>
            <ColorPicker
              value={{
                accent: data[BUTTON_PROP.BACKGROUND_COLOR],
                text: data[BUTTON_PROP.LABEL_COLOR],
              }}
              onChange={(clr) => handleAccentColorChange(clr)}
            />
          </OptionContent>
        </OptionContainer>
      </SettingsOptionContainer>

      {hasStyleguidePermission && (
        <SettingsOptionContainer style={{ border: "none", marginTop: "auto" }}>
          <OptionContainer>
            <AddToLibraryButton
              onClick={(action) =>
                handleButtonLibraryRequest(LIBRARY_SERVICE_ACTION[action])
              }
              isSaved={!!selectedLibraryButton}
              isLoading={isRequestingToLibrary}
              hasChanges={
                selectedLibraryButton
                  ? !isEqual(selectedLibraryButton, data)
                  : false
              }
              macroName="button"
            />
          </OptionContainer>
        </SettingsOptionContainer>
      )}
    </>
  );
};

export default ButtonSettings;
